import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-store';
import { useResellerContext } from '@/lib/reseller-context-store';

/**
 * RequireResellerContext
 * ─────────────────────────────────────────────────────────────────────
 * The first step of the super_admin drill-down. Wraps routes that only
 * make sense once a reseller has been selected (primarily /customers —
 * everything downstream is already guarded by RequireCustomerContext,
 * which the user reaches only after this guard has let them through).
 *
 * Behaviour by role:
 *   - super_admin:
 *       • With a reseller context entered → render children as normal.
 *       • Without → bounce to /resellers so they pick one first.
 *   - reseller_owner and everyone else:
 *       • Renders children unchanged. reseller_owner has an implicit
 *         reseller scope (their own) from the auth-store tenant;
 *         office_manager / standard_user / limited_user / agent are
 *         below that and never need this guard. Bouncing them would
 *         strand them on /resellers which they don't have access to.
 *
 * Do NOT wrap platform-global pages with this guard (carriers, DIDs,
 * plans, platform admin, system prompts) — those operate across all
 * resellers and must remain reachable without drilling in.
 */
export function RequireResellerContext({ children }: { children: ReactNode }) {
  const user = useAuth((s) => s.user);
  const context = useResellerContext((s) => s.current);

  // Not logged in — the parent RequireAuth handles this; belt-and-braces.
  if (!user) return <Navigate to="/login" replace />;

  // Only super_admin gets bounced. reseller_owner and below pass through.
  if (user.role === 'super_admin' && !context) {
    return <Navigate to="/resellers" replace />;
  }

  return <>{children}</>;
}
